import { useState, useEffect, useRef, useCallback, useMemo, type ComponentType, type ReactNode } from 'react';
import {
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  BoltIcon,
  LinkIcon,
  VideoCameraIcon,
  RectangleGroupIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Modal, ConfirmDialog, SeverityTag, Tooltip,
  PageTitle, PAGE_FILL_H, FilterColumn, FilterSection, FilterCheck, FilterSearch, useFilterColumn,
  DEFAULT_RULE_NOTIFICATIONS, ruleNotificationProblem,
  AlertRuleForm, applyRuleRouting, EventTypesSummary, conditionsProblem, conditionsSummary,
  type EventCatalogEntry,
} from '@trinetra/shared';
import {
  getNodeAlertConfigs,
  createNodeAlertConfig,
  updateNodeAlertConfig,
  toggleNodeAlertConfig,
  duplicateNodeAlertConfig,
  deleteNodeAlertConfig,
  getNodeCameras,
  getNodeGroups,
  getNodeEventCatalog,
  getNodeAlertActionsSite,
  nodeRoutingApi,
} from '../services/api.service';
import {
  EVENT_TYPE_OPTIONS,
  scopeOf,
  eventTypeLabel,
  parseEventTypes,
} from '../constants/alertRules';

// A new rule notifies the Dashboard. Email / WhatsApp are not the rule's: Routing policies decide who gets them.
const blankConfig = () => ({
  name: '', event_type: '*', severity: '*', camera_id: null, group_id: null,
  min_confidence: 0.5, cooldown_seconds: 30, description: '',
  conditions: null, sustain_seconds: 0, message_template: '',
  ...DEFAULT_RULE_NOTIFICATIONS,
  webhook_enabled: false, webhook_url: '', is_active: true,
});

/** Normalize form → API payload: clamp confidence, coerce numbers, enforce scope exclusivity. */
function normalize(form: any) {
  const scope = scopeOf(form);
  // __routing is the form's Routing picks — applied separately, never part of the rule.
  const { __routing, ...rule } = form;
  return {
    ...rule,
    // Empty selection means "all event types" — persist the wildcard so an empty rule
    // isn't silently dead (an empty event_type matches nothing at evaluation time).
    event_type: parseEventTypes(form.event_type).join(',') || '*',
    camera_id: scope === 'camera' ? form.camera_id : null,
    group_id: scope === 'group' ? form.group_id : null,
    camera_ids: scope === 'cameras' ? form.camera_ids : null,
    min_confidence: Math.min(1, Math.max(0, parseFloat(form.min_confidence) || 0)),
    cooldown_seconds: Math.max(0, parseInt(form.cooldown_seconds, 10) || 0),
    sustain_seconds: Math.max(0, parseInt(form.sustain_seconds, 10) || 0),
    message_template: String(form.message_template || '').trim() || null,
  };
}

/** The message an operator can act on, out of whatever shape the failure arrived in. */
function errorText(err: any, fallback: string): string {
  return (
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

type StatusFilter = 'all' | 'enabled' | 'disabled';

/** Embedded = the "AI rules" part of the Alert page's Alert Config tab: no title bar of its own. */
export const EMBEDDED_RULES_H = 'h-[calc(100vh-15rem)] min-h-[24rem]';

/**
 * `siteId` / `siteName`: the site whose rules to show — the Alert page picks a site in its own
 * list. Without them, the title-bar switcher's site (the routed page).
 */
export default function AnalyticsConfigPage({ embedded = false, siteId, siteName }: { embedded?: boolean; siteId?: string; siteName?: string } = {}) {
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  // Creating / editing / toggling / duplicating / deleting alert rules is gated
  // on alerts.config; the backend enforces it too. View stays open to any viewer.
  const canConfig = hasPermission('alerts.config');
  const nodeId = siteId ?? (selectedNode?.nodeId || '');
  const nodeName = siteId ? siteName || siteId : selectedNode?.name || nodeId;

  const [configs, setConfigs] = useState<any[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  // What the node's AI module has actually sent — event types and their fields for the rule form.
  const [catalog, setCatalog] = useState<EventCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  // One modal serves both create and edit: `null` = closed, `{ rule: null }` = new rule.
  // Editing used to expand the row into an inline form, which pushed every rule below it off
  // screen and gave a half-finished edit nowhere to live but the list.
  const [formFor, setFormFor] = useState<{ rule: any | null } | null>(null);
  const [deleteFor, setDeleteFor] = useState<any | null>(null);
  // Rules whose enable/disable call is in flight — keyed by id so one pending toggle can't
  // freeze the whole table.
  const [togglingIds, setTogglingIds] = useState<string[]>([]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const fetchConfigs = useCallback(async () => {
    if (!nodeId) { setConfigs([]); setLoading(false); return; }
    setLoading(true);
    try {
      setConfigs(await getNodeAlertConfigs(nodeId) || []);
      setPageError('');
    } catch (err) {
      console.error('Failed to fetch configs:', err);
      setPageError(errorText(err, 'Failed to load alert rules'));
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  /**
   * A rule form (or a delete confirmation) open across a site switch is pointed at a rule id
   * belonging to the site just left — its Save would write to the new node under an id that
   * node has never issued. The rules list refetches above; these have to be closed.
   */
  const firstNodeRef = useRef(true);
  useEffect(() => {
    if (firstNodeRef.current) { firstNodeRef.current = false; return; }
    setFormFor(null);
    setDeleteFor(null);
    setTogglingIds([]);
    setPageError('');
  }, [nodeId]);

  // Cameras + groups feed the scope pickers in the rule form.
  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId).then((c: any) => setCameras(Array.isArray(c) ? c : c?.data || [])).catch(() => setCameras([]));
    getNodeGroups(nodeId).then((g: any) => setGroups(Array.isArray(g) ? g : g?.data || [])).catch(() => setGroups([]));
    getNodeEventCatalog(nodeId).then(setCatalog).catch(() => setCatalog([]));
  }, [nodeId]);

  const handleToggle = async (cfg: any) => {
    setTogglingIds((prev) => [...prev, cfg.id]);
    // Optimistic: the switch is the one control an operator flips repeatedly, and waiting a
    // tunnel round trip for it to move reads as a dead toggle. Reverted below if it fails.
    setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: !c.is_active } : c));
    try {
      const updated = await toggleNodeAlertConfig(nodeId, cfg.id);
      setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: updated.is_active } : c));
      setPageError('');
    } catch (err) {
      console.error('Toggle failed:', err);
      setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: cfg.is_active } : c));
      setPageError(errorText(err, `Failed to ${cfg.is_active ? 'disable' : 'enable'} this rule`));
    } finally {
      setTogglingIds((prev) => prev.filter((id) => id !== cfg.id));
    }
  };

  const handleDelete = async () => {
    if (!deleteFor) return;
    try {
      await deleteNodeAlertConfig(nodeId, deleteFor.id);
      setConfigs((prev) => prev.filter((c) => c.id !== deleteFor.id));
      setDeleteFor(null);
      setPageError('');
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteFor(null);
      setPageError(errorText(err, 'Failed to delete this rule'));
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const clone = await duplicateNodeAlertConfig(nodeId, id);
      setConfigs((prev) => [clone, ...prev]);
      setPageError('');
    } catch (err) {
      console.error('Duplicate failed:', err);
      setPageError(errorText(err, 'Failed to duplicate this rule'));
    }
  };

  // Both submit paths throw on failure: the modal stays open and shows why, instead of
  // closing on a save that never happened.
  const handleSubmit = async (data: any) => {
    let saved: any;
    if (formFor?.rule) {
      saved = await updateNodeAlertConfig(nodeId, formFor.rule.id, normalize(data));
      setConfigs((prev) => prev.map((c) => c.id === formFor.rule.id ? saved : c));
    } else {
      saved = await createNodeAlertConfig(nodeId, normalize(data));
      setConfigs((prev) => [saved, ...prev]);
    }
    setPageError('');
    // "Where should it go?" — the site's Routing notifications that list this rule.
    if (Array.isArray(data.__routing) && saved?.id) {
      await applyRuleRouting(nodeRoutingApi(nodeId), String(saved.id), data.__routing)
        .catch((err: any) => setPageError(`The rule is saved, but Routing was not updated: ${err?.response?.data?.message || err?.message || 'unknown error'}`));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return configs.filter((c) => {
      if (statusFilter === 'enabled' && !c.is_active) return false;
      if (statusFilter === 'disabled' && c.is_active) return false;
      if (!q) return true;
      // Searches what's on screen: the rule's own words plus the event types it targets.
      return [c.name, c.description, eventTypeLabel(c.event_type), c.event_type, c.severity]
        .filter(Boolean)
        .some((v: any) => String(v).toLowerCase().includes(q));
    });
  }, [configs, search, statusFilter]);

  const enabledCount = configs.filter((c) => c.is_active).length;
  const filterCol = useFilterColumn('alert-rules');

  if (!nodeId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-6">
        Select a site to configure its alert rules.
      </div>
    );
  }

  const subtitle = <>{configs.length} {configs.length === 1 ? 'rule' : 'rules'} · {enabledCount} enabled · Decide which AI detections on <span className="font-medium">{nodeName}</span> become alerts.</>;
  const actions = (
    <>
        {canConfig && (
          <button
            data-tour="alert-rules-new"
            onClick={() => setFormFor({ rule: null })}
            className="flex h-8 items-center gap-1 px-3 rounded-lg bg-brand text-white text-[13px] hover:opacity-90"
          >
            <PlusIcon className="w-4 h-4" /> New Rule
          </button>
        )}
        <Tooltip label="Reload rules" side="bottom">
          <button onClick={fetchConfigs} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted text-muted-foreground" aria-label="Reload rules">
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </Tooltip>
    </>
  );

  return (
    <div className={`flex items-start gap-3 ${embedded ? EMBEDDED_RULES_H : PAGE_FILL_H}`}>
      {!embedded && <PageTitle title="Alert Rules" subtitle={subtitle}>{actions}</PageTitle>}

      {/* ── Filters: a site with 40 rules is a list you scan, not one you read ── */}
      <FilterColumn
        open={filterCol.open}
        onToggle={filterCol.toggle}
        label="Rule filters"
        heightClass="h-full"
        activeCount={(search ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0)}
        onClearAll={() => { setSearch(''); setStatusFilter('all'); }}
      >
        <FilterSearch value={search} onChange={setSearch} placeholder="Search rules, event types…" />
        <FilterSection title="State">
          <div data-tour="alert-rules-filters" className="space-y-0.5">
            {([['all', 'All'], ['enabled', 'Enabled'], ['disabled', 'Disabled']] as [StatusFilter, string][]).map(([k, label]) => (
              <FilterCheck key={k} radio checked={statusFilter === k} onClick={() => setStatusFilter(k)} label={label} />
            ))}
          </div>
        </FilterSection>
      </FilterColumn>

      <div className="flex h-full min-w-0 flex-1 flex-col">
      {embedded && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <p className="min-w-0 flex-1 text-[12px] text-muted-foreground">{subtitle}</p>
          {actions}
        </div>
      )}
      {(search || statusFilter !== 'all') && (
        <div className="mb-2 text-[12px] text-muted-foreground">
          {filtered.length} of {configs.length} shown
        </div>
      )}

      {pageError && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">{pageError}</span>
          <button onClick={() => setPageError('')} className="text-[12px] underline opacity-80 hover:opacity-100">Dismiss</button>
        </div>
      )}

      {/* ── The rules ── */}
      <div data-tour="alert-rules-table" className="flex-1 min-h-0 overflow-hidden rounded-xl border border-border bg-card">
        <RuleTable
          rules={filtered}
          catalog={catalog}
          totalCount={configs.length}
          cameras={cameras}
          groups={groups}
          canConfig={canConfig}
          loading={loading}
          togglingIds={togglingIds}
          onToggle={handleToggle}
          onEdit={(rule: any) => setFormFor({ rule })}
          onDuplicate={handleDuplicate}
          onDelete={setDeleteFor}
          onCreate={() => setFormFor({ rule: null })}
          onClearFilters={() => { setSearch(''); setStatusFilter('all'); }}
        />
      </div>

      </div>

      {/* ── Create / edit, same form either way ── */}
      <RuleFormModal
        open={!!formFor}
        rule={formFor?.rule || null}
        cameras={cameras}
        groups={groups}
        catalog={catalog}
        nodeId={nodeId}
        onSubmit={handleSubmit}
        onClose={() => setFormFor(null)}
      />

      <ConfirmDialog
        open={!!deleteFor}
        title="Delete alert rule"
        message={
          <>
            Detections matching <span className="font-medium text-foreground">{ruleTitle(deleteFor)}</span> will
            no longer raise alerts. Alerts already raised are kept.
          </>
        }
        confirmLabel="Delete rule"
        onConfirm={handleDelete}
        onCancel={() => setDeleteFor(null)}
      />
    </div>
  );
}

// ─── Row vocabulary ───────────────────────────────────────────

function ruleTitle(config: any): string {
  if (!config) return '';
  return config.name || `Rule: ${eventTypeLabel(config.event_type)}`;
}
const cameraName = (cams: any[], id: string) => cams.find((c) => c.id === id)?.name || id;
const groupName = (grps: any[], id: string) => grps.find((g) => g.id === id)?.name || id;

/** Scope as icon + text, so the column can be read down rather than word by word. */
function ScopeCell({ config, cameras, groups }: { config: any; cameras: any[]; groups: any[] }) {
  const scope = scopeOf(config);
  const Icon = scope === 'camera' || scope === 'cameras' ? VideoCameraIcon : scope === 'group' ? RectangleGroupIcon : GlobeAltIcon;
  const listed: string[] = scope === 'cameras' ? config.camera_ids || [] : [];
  const text = scope === 'camera'
    ? cameraName(cameras, config.camera_id)
    : scope === 'cameras'
      ? (listed.length === 1 ? cameraName(cameras, listed[0]) : `${listed.length} cameras: ${listed.map((id) => cameraName(cameras, id)).join(', ')}`)
      : scope === 'group'
        ? groupName(groups, config.group_id)
        : 'All cameras';
  return (
    <div className="flex min-w-0 items-center gap-1.5" title={text}>
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate text-foreground/90">{text}</span>
    </div>
  );
}

const NOTIFY_CHANNELS: { key: string; icon: ComponentType<{ className?: string }>; label: string; tone: string }[] = [
  { key: 'websocket_enabled', icon: BoltIcon, label: 'Dashboard', tone: 'text-emerald-500' },
  { key: 'webhook_enabled', icon: LinkIcon, label: 'Webhook', tone: 'text-purple-400' },
];

/**
 * The rule's own channels (Dashboard, Webhook) in fixed positions, lit or faint. Email /
 * WhatsApp are Routing policies, not the rule's, so they are not shown here.
 *
 * Rendering only the enabled ones (what the card list did) shifted the icons from row to row,
 * so "does this rule notify anyone?" meant reading every badge instead of glancing down one
 * column.
 */
function NotifyCell({ config }: { config: any }) {
  return (
    <div className="flex items-center gap-1.5">
      {NOTIFY_CHANNELS.map(({ key, icon: Icon, label, tone }) => {
        const on = key === 'websocket_enabled' ? config[key] !== false : !!config[key];
        return (
          <Tooltip key={key} label={`${label} — ${on ? 'on' : 'off'}`} side="top">
            <span className={on ? tone : 'text-muted-foreground opacity-25'}>
              <Icon className="h-4 w-4" />
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** Enable/disable switch. Read-only rendering for a viewer without alerts.config. */
function EnableSwitch({ checked, busy, disabled, label, onChange }: {
  checked?: boolean; busy?: boolean; disabled?: boolean; label: string; onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled || busy}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-brand' : 'bg-muted border border-border'
      } ${disabled ? 'cursor-default opacity-70' : 'cursor-pointer'} ${busy ? 'opacity-60' : ''}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[1.15rem]' : 'translate-x-[0.15rem]'
        }`}
      />
    </button>
  );
}

const TH = 'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';

function RuleTable({
  rules, catalog, totalCount, cameras, groups, canConfig, loading, togglingIds,
  onToggle, onEdit, onDuplicate, onDelete, onCreate, onClearFilters,
}: any) {
  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />)}
      </div>
    );
  }

  if (rules.length === 0) {
    // Two different empties: nothing configured yet, versus filters that hide everything.
    const filtered = totalCount > 0;
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <Cog6ToothIcon className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium text-foreground">
          {filtered ? 'No rules match these filters' : 'No alert rules yet'}
        </p>
        <p className="text-[13px] text-muted-foreground">
          {filtered
            ? 'Clear the search or state filter to see the rest.'
            : 'Until a rule matches it, an AI detection is recorded but nobody is told.'}
        </p>
        {filtered ? (
          <button onClick={onClearFilters} className="mt-1 text-sm text-brand">Clear filters</button>
        ) : canConfig ? (
          <button onClick={onCreate} className="mt-1 text-sm text-brand">Create your first rule</button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <table className="w-full min-w-[64rem] table-fixed text-[13px]">
        <colgroup>
          <col className="w-[24%]" />
          <col className="w-[18%]" />
          <col className="w-[14%]" />
          <col className="w-[9%]" />
          <col className="w-[11%]" />
          <col className="w-[9%]" />
          <col className="w-[7%]" />
          <col className="w-[8%]" />
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-border bg-muted/60 backdrop-blur">
            <th className={`${TH} pl-4`}>Rule</th>
            <th className={TH}>Event types</th>
            <th className={TH}>Applies to</th>
            <th className={TH}>Severity</th>
            <th className={TH}>Threshold</th>
            <th className={TH}>Notify</th>
            <th className={TH}>Enabled</th>
            <th className={`${TH} pr-4 text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((cfg: any) => {
            const busy = togglingIds.includes(cfg.id);
            return (
              <tr
                key={cfg.id}
                onClick={canConfig ? () => onEdit(cfg) : undefined}
                className={`border-b border-border transition-colors last:border-0 hover:bg-muted/40 ${
                  canConfig ? 'cursor-pointer' : ''
                } ${cfg.is_active ? '' : 'opacity-60'}`}
              >
                <td className="py-2.5 pl-4 pr-3">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-foreground" title={ruleTitle(cfg)}>
                      {ruleTitle(cfg)}
                    </div>
                    {cfg.description && (
                      <div className="mt-0.5 truncate text-[12px] text-muted-foreground" title={cfg.description}>
                        {cfg.description}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <EventTypesSummary value={cfg.event_type} builtin={EVENT_TYPE_OPTIONS} catalog={catalog} />
                  {conditionsSummary(cfg) && (
                    <div className="mt-1 truncate text-[11px] text-muted-foreground" title={conditionsSummary(cfg)}>
                      when {conditionsSummary(cfg)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5"><ScopeCell config={cfg} cameras={cameras} groups={groups} /></td>
                <td className="px-3 py-2.5">
                  {cfg.severity && cfg.severity !== '*'
                    ? <SeverityTag severity={cfg.severity} variant="soft" dot={false} />
                    : <span className="text-[12px] text-muted-foreground">Auto</span>}
                </td>
                <td className="px-3 py-2.5">
                  <div className="text-foreground/90">≥ {((cfg.min_confidence || 0) * 100).toFixed(0)}%</div>
                  <div className="text-[12px] text-muted-foreground">{cfg.cooldown_seconds ?? 0}s cooldown</div>
                </td>
                <td className="px-3 py-2.5"><NotifyCell config={cfg} /></td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <EnableSwitch
                    checked={cfg.is_active}
                    busy={busy}
                    disabled={!canConfig}
                    label={`${cfg.is_active ? 'Disable' : 'Enable'} ${ruleTitle(cfg)}`}
                    onChange={() => onToggle(cfg)}
                  />
                </td>
                <td className="px-3 py-2.5 pr-4" onClick={(e) => e.stopPropagation()}>
                  {canConfig ? (
                    <div className="flex items-center justify-end gap-0.5">
                      <Tooltip label="Edit rule" side="top">
                        <button onClick={() => onEdit(cfg)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Edit rule">
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip label="Duplicate rule" side="top">
                        <button onClick={() => onDuplicate(cfg.id)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Duplicate rule">
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip label="Delete rule" side="top">
                        <button onClick={() => onDelete(cfg)} className="rounded p-1.5 text-red-400 hover:bg-red-500/10" aria-label="Delete rule">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  ) : (
                    <div className="text-right text-[12px] text-muted-foreground">—</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── The form ─────────────────────────────────────────────────

/** What can't be saved, said in the operator's terms rather than the API's. */
function validate(form: any, handledBy: 'site' | 'cloud' | null = null): string {
  const scope = scopeOf(form);
  if (scope === 'camera' && !form.camera_id) return 'Pick the camera this rule applies to.';
  if (scope === 'group' && !form.group_id) return 'Pick the group this rule applies to.';
  if (scope === 'cameras' && !(form.camera_ids || []).length) return 'Pick at least one camera.';
  const condProblem = conditionsProblem(form);
  if (condProblem) return condProblem;
  const notifyProblem = ruleNotificationProblem(form, handledBy);
  if (notifyProblem) return notifyProblem;
  if (form.webhook_enabled && !String(form.webhook_url || '').trim()) return 'Add the webhook URL, or turn Webhook off.';
  return '';
}

/**
 * Create AND edit. One form for both, so the two can't drift, and an edit no longer expands
 * the row it belongs to — which pushed the rest of the table off screen mid-edit.
 */
function RuleFormModal({ open, rule, cameras, groups, catalog, nodeId, onSubmit, onClose }: {
  open: boolean;
  rule: any | null;
  cameras: any[];
  groups: any[];
  catalog: EventCatalogEntry[];
  nodeId: string;
  onSubmit: (data: any) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<any>(blankConfig);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Who sends this site's notifications (its Alert Actions switch): with the cloud, a rule's
  // recipients are optional, and the Notifications section says where its email goes.
  const [handledBy, setHandledBy] = useState<'site' | 'cloud' | null>(null);

  useEffect(() => {
    if (!open || !nodeId) return;
    getNodeAlertActionsSite(nodeId)
      .then((s: any) => setHandledBy(s?.handled_by === 'cloud' ? 'cloud' : 'site'))
      .catch(() => setHandledBy(null));
  }, [open, nodeId]);

  // Reset on open, and whenever the modal is pointed at a different rule.
  useEffect(() => {
    if (!open) return;
    setForm(rule ? { ...rule } : blankConfig());
    setError('');
    setSaving(false);
  }, [open, rule]);

  // Kept stable: a new API object each render would reload the notifications every render.
  const routing = useMemo(() => ({ api: nodeRoutingApi(nodeId), ruleId: rule?.id ? String(rule.id) : null }), [nodeId, rule?.id]);
  const problem = validate(form, handledBy);

  async function handleSave() {
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit(form);
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
      setError(errorText(err, 'Failed to save this rule'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={rule ? 'Edit alert rule' : 'New alert rule'}
      subtitle={rule ? ruleTitle(rule) : 'Answer a few questions — the sentence at the top says what the rule will do.'}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 flex-1 truncate text-[12px] text-destructive">{error}</span>
          <div className="flex shrink-0 items-center gap-2">
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-border px-4 py-2 text-[13px] text-muted-foreground hover:bg-muted disabled:opacity-50">
              Cancel
            </button>
            <Tooltip label={problem || ''} side="top">
              <button
                onClick={handleSave}
                disabled={saving || !!problem}
                className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {saving && <span className="h-3.5 w-3.5 animate-spin rounded-full border-t-2 border-white" />}
                {saving ? 'Saving…' : rule ? 'Save changes' : 'Create rule'}
              </button>
            </Tooltip>
          </div>
        </div>
      }
    >
      <AlertRuleForm
        form={form} setForm={setForm} cameras={cameras} groups={groups} catalog={catalog || []}
        builtin={EVENT_TYPE_OPTIONS} routingHref={`/alert-actions?tab=actions&site=${encodeURIComponent(nodeId)}`}
        routing={routing}
      />
    </Modal>
  );
}
